import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import {
    PiPlusBold,
    PiPencilSimpleBold,
    PiTrashBold,
    PiUserBold,
    PiEnvelopeBold,
    PiPhoneBold,
    PiIdentificationCardBold,
    PiMapPinBold,
    PiCheckCircleBold,
    PiXCircleBold,
    PiXBold,
    PiFloppyDiskBold,
    PiBuildingsBold,
    PiWarningCircleBold,
    PiListBold,
    PiNotepadBold,
    PiEyeBold,
} from 'react-icons/pi';
import api from '@v2/lib/api';
import { PageHeader } from '@v2/design-system';
import TablePagination, { TABLE_PER_PAGE_OPTIONS } from '@v2/components/TablePagination';
import { cn } from '@v2/utils/cn';

/* ─── Types ─── */
type CustomsBroker = {
    id: number;
    name: string;
    cnpj: string | null;
    contact_person: string | null;
    phone: string | null;
    email: string | null;
    email_secondary: string | null;
    address: string | null;
    notes: string | null;
    active: boolean;
    sort_order: number;
    created_at: string | null;
};

type FormValues = Omit<CustomsBroker, 'id' | 'created_at'>;

type Meta = { total: number; last_page: number; current_page: number; per_page: number; from?: number | null; to?: number | null };

/* ─── API helpers ─── */
const fetchBrokers = async (page: number, perPage: number, search: string) => {
    const { data } = await api.get<{ data: CustomsBroker[]; meta: Meta }>(
        '/customs-brokers',
        { params: { page, per_page: perPage, search: search || undefined } },
    );
    return data;
};

/* ─── Stat Card ─── */
function StatCard({ icon, label, value, color }: { icon: React.ReactNode; label: string; value: number | string; color: string }) {
    return (
        <div className="rounded-xl border border-muted bg-background p-4 flex items-center gap-4 shadow-sm">
            <div className={cn('flex h-11 w-11 items-center justify-center rounded-lg text-white', color)}>
                {icon}
            </div>
            <div>
                <p className="text-xl font-bold text-foreground">{value}</p>
                <p className="text-xs text-muted-foreground">{label}</p>
            </div>
        </div>
    );
}

/* ─── Broker Card ─── */
function BrokerCard({ broker, onEdit, onDelete, onView }: { broker: CustomsBroker; onEdit: () => void; onDelete: () => void; onView: () => void }) {
    return (
        <div className="rounded-xl border border-muted bg-background shadow-sm hover:shadow-md transition-shadow flex flex-col">
            {/* Header */}
            <div className="flex items-start justify-between p-5 border-b border-muted">
                <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 shrink-0">
                        <PiBuildingsBold className="h-5 w-5" />
                    </div>
                    <div>
                        <h3 className="font-semibold text-foreground leading-tight">{broker.name}</h3>
                        {broker.cnpj && (
                            <p className="text-xs text-muted-foreground mt-0.5 font-mono">
                                {broker.cnpj}
                            </p>
                        )}
                    </div>
                </div>
                <span className={cn(
                    'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium shrink-0',
                    broker.active
                        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                        : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
                )}>
                    {broker.active ? <PiCheckCircleBold className="h-3 w-3" /> : <PiXCircleBold className="h-3 w-3" />}
                    {broker.active ? 'Ativo' : 'Inativo'}
                </span>
            </div>

            {/* Body */}
            <div className="p-5 space-y-2 flex-1 text-sm">
                {broker.contact_person && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                        <PiUserBold className="h-4 w-4 shrink-0" />
                        <span>{broker.contact_person}</span>
                    </div>
                )}
                {broker.phone && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                        <PiPhoneBold className="h-4 w-4 shrink-0" />
                        <a href={`tel:${broker.phone}`} className="hover:text-foreground transition-colors">
                            {broker.phone}
                        </a>
                    </div>
                )}
                {broker.email && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                        <PiEnvelopeBold className="h-4 w-4 shrink-0" />
                        <a href={`mailto:${broker.email}`} className="hover:text-foreground transition-colors truncate">
                            {broker.email}
                        </a>
                    </div>
                )}
                {broker.email_secondary && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                        <PiEnvelopeBold className="h-4 w-4 shrink-0 opacity-60" />
                        <a href={`mailto:${broker.email_secondary}`} className="hover:text-foreground transition-colors truncate text-xs">
                            {broker.email_secondary}
                        </a>
                    </div>
                )}
                {broker.address && (
                    <div className="flex items-start gap-2 text-muted-foreground">
                        <PiMapPinBold className="h-4 w-4 shrink-0 mt-0.5" />
                        <span className="text-xs leading-relaxed">{broker.address}</span>
                    </div>
                )}
            </div>

            {/* Footer actions */}
            <div className="flex items-center justify-between gap-2 px-5 py-3 border-t border-muted">
                <button
                    onClick={onView}
                    className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium text-primary hover:bg-primary/10 transition-colors cursor-pointer"
                >
                    <PiEyeBold className="h-3.5 w-3.5" />
                    Visualizar
                </button>
                <div className="flex items-center gap-1">
                    <button
                        onClick={onEdit}
                        className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors cursor-pointer"
                    >
                        <PiPencilSimpleBold className="h-3.5 w-3.5" />
                        Editar
                    </button>
                    <button
                        onClick={onDelete}
                        className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors cursor-pointer"
                    >
                        <PiTrashBold className="h-3.5 w-3.5" />
                        Excluir
                    </button>
                </div>
            </div>
        </div>
    );
}

/* ─── Form Drawer ─── */
function BrokerFormDrawer({
    editing,
    onClose,
}: {
    editing: CustomsBroker | null;
    onClose: () => void;
}) {
    const qc = useQueryClient();
    const isEdit = !!editing;

    const { register, handleSubmit, formState: { errors } } = useForm<FormValues>({
        defaultValues: {
            name: editing?.name ?? '',
            cnpj: editing?.cnpj ?? '',
            contact_person: editing?.contact_person ?? '',
            phone: editing?.phone ?? '',
            email: editing?.email ?? '',
            email_secondary: editing?.email_secondary ?? '',
            address: editing?.address ?? '',
            notes: editing?.notes ?? '',
            active: editing?.active ?? true,
            sort_order: editing?.sort_order ?? 0,
        },
    });

    const mutation = useMutation({
        mutationFn: (values: FormValues) =>
            isEdit
                ? api.patch(`/customs-brokers/${editing!.id}`, values)
                : api.post('/customs-brokers', values),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['customs-brokers'] });
            onClose();
        },
    });

    const inputCls = 'w-full rounded-lg border border-muted bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40';
    const labelCls = 'block text-xs font-medium text-muted-foreground mb-1';
    const errorCls = 'mt-1 text-xs text-red-500';

    return (
        <div className="fixed inset-0 z-50 flex items-stretch justify-end">
            {/* Overlay */}
            <div className="absolute inset-0 bg-black/40" onClick={onClose} />

            {/* Drawer */}
            <div className="relative z-10 flex flex-col w-full max-w-xl bg-background shadow-2xl overflow-y-auto">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-muted">
                    <div>
                        <h2 className="text-base font-semibold text-foreground">
                            {isEdit ? 'Editar Despachante' : 'Novo Despachante'}
                        </h2>
                        <p className="text-xs text-muted-foreground mt-0.5">
                            {isEdit ? editing!.name : 'Preencha os dados do despachante aduaneiro'}
                        </p>
                    </div>
                    <button onClick={onClose} className="rounded-lg p-2 hover:bg-muted transition-colors cursor-pointer">
                        <PiXBold className="h-4 w-4 text-muted-foreground" />
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit((v) => mutation.mutate(v))} className="flex-1 overflow-y-auto">
                    <div className="px-6 py-5 space-y-6">

                        {/* Identificação */}
                        <section>
                            <h3 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-4">
                                <PiIdentificationCardBold className="h-4 w-4" />
                                Identificação
                            </h3>
                            <div className="space-y-4">
                                <div>
                                    <label className={labelCls}>Razão Social <span className="text-red-500">*</span></label>
                                    <input {...register('name', { required: 'Obrigatório' })} className={inputCls} placeholder="Nome completo do despachante" />
                                    {errors.name && <p className={errorCls}>{errors.name.message}</p>}
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className={labelCls}>CNPJ</label>
                                        <input {...register('cnpj')} className={inputCls} placeholder="00.000.000/0000-00" />
                                    </div>
                                    <div>
                                        <label className={labelCls}>Responsável / Contato</label>
                                        <input {...register('contact_person')} className={inputCls} placeholder="Nome do contato" />
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* Contato */}
                        <section>
                            <h3 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-4">
                                <PiPhoneBold className="h-4 w-4" />
                                Contato
                            </h3>
                            <div className="space-y-4">
                                <div>
                                    <label className={labelCls}>Telefone</label>
                                    <input {...register('phone')} className={inputCls} placeholder="(00) 00000-0000" />
                                </div>
                                <div>
                                    <label className={labelCls}>E-mail Principal</label>
                                    <input {...register('email')} type="email" className={inputCls} placeholder="contato@despachante.com.br" />
                                </div>
                                <div>
                                    <label className={labelCls}>E-mail Secundário</label>
                                    <input {...register('email_secondary')} type="email" className={inputCls} placeholder="outro@despachante.com.br" />
                                </div>
                            </div>
                        </section>

                        {/* Endereço */}
                        <section>
                            <h3 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-4">
                                <PiMapPinBold className="h-4 w-4" />
                                Endereço
                            </h3>
                            <textarea
                                {...register('address')}
                                rows={3}
                                className={inputCls}
                                placeholder="Endereço completo"
                            />
                        </section>

                        {/* Observações */}
                        <section>
                            <h3 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-4">
                                <PiNotepadBold className="h-4 w-4" />
                                Observações Internas
                            </h3>
                            <textarea
                                {...register('notes')}
                                rows={3}
                                className={inputCls}
                                placeholder="Notas internas sobre este despachante..."
                            />
                        </section>

                        {/* Configurações */}
                        <section>
                            <h3 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-4">
                                <PiListBold className="h-4 w-4" />
                                Configurações
                            </h3>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className={labelCls}>Ordem de exibição</label>
                                    <input {...register('sort_order', { valueAsNumber: true })} type="number" className={inputCls} placeholder="0" />
                                </div>
                                <div className="flex items-center gap-3 pt-5">
                                    <input
                                        id="active"
                                        type="checkbox"
                                        {...register('active')}
                                        className="h-4 w-4 rounded border-muted accent-primary cursor-pointer"
                                    />
                                    <label htmlFor="active" className="text-sm font-medium text-foreground cursor-pointer">
                                        Despachante ativo
                                    </label>
                                </div>
                            </div>
                        </section>
                    </div>

                    {/* Footer */}
                    <div className="sticky bottom-0 bg-background border-t border-muted px-6 py-4 flex items-center justify-end gap-3">
                        <button type="button" onClick={onClose} className="rounded-lg px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-muted transition-colors cursor-pointer">
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            disabled={mutation.isPending}
                            className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-60 transition-colors cursor-pointer"
                        >
                            <PiFloppyDiskBold className="h-4 w-4" />
                            {mutation.isPending ? 'Salvando…' : 'Salvar'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

/* ─── Delete Confirm ─── */
function DeleteConfirm({ target, onClose }: { target: CustomsBroker; onClose: () => void }) {
    const qc = useQueryClient();

    const mutation = useMutation({
        mutationFn: () => api.delete(`/customs-brokers/${target.id}`),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['customs-brokers'] });
            onClose();
        },
    });

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/40" onClick={onClose} />
            <div className="relative z-10 w-full max-w-sm rounded-xl bg-background border border-muted shadow-2xl p-6">
                <div className="flex items-center gap-3 mb-4">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
                        <PiWarningCircleBold className="h-5 w-5 text-red-600 dark:text-red-400" />
                    </div>
                    <div>
                        <h3 className="font-semibold text-foreground">Excluir despachante</h3>
                        <p className="text-xs text-muted-foreground">Esta ação não pode ser desfeita</p>
                    </div>
                </div>
                <p className="text-sm text-muted-foreground mb-6">
                    Tem certeza que deseja excluir <strong className="text-foreground">{target.name}</strong>?
                </p>
                <div className="flex gap-3 justify-end">
                    <button onClick={onClose} className="rounded-lg px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-muted transition-colors cursor-pointer">
                        Cancelar
                    </button>
                    <button
                        onClick={() => mutation.mutate()}
                        disabled={mutation.isPending}
                        className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-60 transition-colors cursor-pointer"
                    >
                        {mutation.isPending ? 'Excluindo…' : 'Excluir'}
                    </button>
                </div>
            </div>
        </div>
    );
}

/* ─── Main Page ─── */
export default function CustomsBrokersPage() {
    const navigate = useNavigate();
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(25);
    const [search, setSearch] = useState('');
    const [formOpen, setFormOpen] = useState(false);
    const [editing, setEditing] = useState<CustomsBroker | null>(null);
    const [deleting, setDeleting] = useState<CustomsBroker | null>(null);

    const { data, isLoading } = useQuery({
        queryKey: ['customs-brokers', page, perPage, search],
        queryFn: () => fetchBrokers(page, perPage, search),
        placeholderData: (prev) => prev,
    });

    const brokers = data?.data ?? [];
    const meta = data?.meta;
    const total = meta?.total ?? 0;
    const active = brokers.filter((b) => b.active).length;
    const inactive = brokers.filter((b) => !b.active).length;

    const openNew = () => { setEditing(null); setFormOpen(true); };
    const openEdit = (b: CustomsBroker) => { setEditing(b); setFormOpen(true); };

    return (
        <div className="space-y-6">
            <PageHeader
                title="Despachantes Aduaneiros"
                breadcrumb={[{ name: 'Configurações' }, { name: 'Despachantes Aduaneiros' }]}
            >
                <button
                    onClick={openNew}
                    className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors cursor-pointer"
                >
                    <PiPlusBold className="h-4 w-4" />
                    Novo Despachante
                </button>
            </PageHeader>

            {/* KPI Cards */}
            {!isLoading && total > 0 && (
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                    <StatCard icon={<PiBuildingsBold className="h-5 w-5" />} label="Total cadastrado" value={total} color="bg-amber-600" />
                    <StatCard icon={<PiCheckCircleBold className="h-5 w-5" />} label="Ativos" value={active} color="bg-green-600" />
                    <StatCard icon={<PiXCircleBold className="h-5 w-5" />} label="Inativos" value={inactive} color="bg-red-500" />
                    <StatCard icon={<PiEnvelopeBold className="h-5 w-5" />} label="Com e-mail" value={brokers.filter((b) => b.email).length} color="bg-blue-600" />
                </div>
            )}

            {/* Search */}
            <div className="flex items-center gap-3">
                <input
                    type="search"
                    placeholder="Buscar por nome, CNPJ, e-mail ou contato…"
                    value={search}
                    onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                    className="w-full max-w-sm rounded-lg border border-muted bg-background px-4 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
                />
                {search && (
                    <button onClick={() => setSearch('')} className="text-xs text-muted-foreground hover:text-foreground cursor-pointer">
                        Limpar
                    </button>
                )}
            </div>

            {/* Content */}
            {isLoading ? (
                <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                    {Array.from({ length: 6 }).map((_, i) => (
                        <div key={i} className="h-52 rounded-xl border border-muted bg-muted/30 animate-pulse" />
                    ))}
                </div>
            ) : brokers.length === 0 ? (
                <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-muted bg-muted/10 py-16">
                    <PiBuildingsBold className="h-12 w-12 text-muted-foreground/40 mb-3" />
                    <p className="text-sm font-medium text-muted-foreground">
                        {search ? 'Nenhum despachante encontrado' : 'Nenhum despachante cadastrado'}
                    </p>
                    {!search && (
                        <button onClick={openNew} className="mt-4 text-sm text-primary hover:underline cursor-pointer">
                            Cadastrar primeiro despachante
                        </button>
                    )}
                </div>
            ) : (
                <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                    {brokers.map((broker) => (
                        <BrokerCard
                            key={broker.id}
                            broker={broker}
                            onView={() => navigate(`/settings/customs-brokers/${broker.id}`)}
                            onEdit={() => openEdit(broker)}
                            onDelete={() => setDeleting(broker)}
                        />
                    ))}
                </div>
            )}

            {/* Pagination */}
            {meta && (
                <TablePagination
                    meta={{
                        current_page: meta.current_page,
                        last_page: meta.last_page,
                        per_page: meta.per_page ?? perPage,
                        total: meta.total,
                        from: meta.from,
                        to: meta.to,
                    }}
                    onPageChange={setPage}
                    onPerPageChange={(value) => {
                        setPerPage(value);
                        setPage(1);
                    }}
                    perPageOptions={[...TABLE_PER_PAGE_OPTIONS]}
                    itemLabel="despachante"
                />
            )}

            {/* Modals */}
            {formOpen && (
                <BrokerFormDrawer
                    editing={editing}
                    onClose={() => { setFormOpen(false); setEditing(null); }}
                />
            )}
            {deleting && (
                <DeleteConfirm target={deleting} onClose={() => setDeleting(null)} />
            )}
        </div>
    );
}
